import { stringify } from 'query-string';
import {
  saveTopicFilters,
  saveTopicFiltersRemote,
} from 'redux/reducers/topicFilters';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { trackFilter } from 'redux/reducers/usage/filter/filter';
import { TopicFilterNames, ITopicFilterState } from '../shared';
import last from 'lodash.last';
import { isArray } from 'lodash';

/**
 * setFilterUrl: this sets the given filter name and value in the URL bar and dispatches a tracking action.
 * clearFilterUrl: this resets the current filter state, or only the given filter name, and serializes it to the URL bar.
 * setStoreToUrl: this serializes the current filter state to the URL bar.
 */
type UrlFilterHandlers = [
  (type: TopicFilterNames, value?: string, id?: string) => void,
  (type?: TopicFilterNames | TopicFilterNames[]) => void
];

/**
 * This hooks provides three methods that deal with updating the URL bar according to the values of the topic filters in the Redux store.
 * @param currentFiltersState these are all the current filter values from the Redux store
 * @returns see the [[UseFilterHandlers]] type for more details.
 */
export function useFilterUrlHandlers(
  currentFiltersState: ITopicFilterState,
  activeFilters: TopicFilterNames[]
): UrlFilterHandlers {
  const history = useHistory();
  const dispatch = useDispatch();

  const setFilterUrl = useCallback(
    (type: TopicFilterNames, value?: string, id?: string) => {
      let formattedValue: number | string | null = null;
      if (
        (type === 'genre' || type === 'country' || type === 'customRegion') &&
        value
      ) {
        formattedValue = value;
      } else if (value) {
        const valueInt = parseInt(value);
        const isString = isNaN(valueInt);
        formattedValue = isString ? value : valueInt;
      }

      // Update the url.
      const newState = { ...currentFiltersState };
      if (formattedValue) {
        newState[type] = formattedValue as any;
      } else {
        delete newState[type];
      }

      history.replace({ search: stringify(newState) });

      // Save the type in the KV store so we can track its usage.
      dispatch(
        trackFilter(
          type,
          Array.isArray(formattedValue) ? last(formattedValue) : formattedValue,
          id
        )
      );
    },
    [history, dispatch, currentFiltersState]
  );

  const deleteActiveFilters = useCallback(
    (currentFiltersState: ITopicFilterState): ITopicFilterState => {
      const newState = { ...currentFiltersState };
      if (activeFilters) {
        activeFilters.forEach((activeFilter: TopicFilterNames) => {
          newState[activeFilter] = undefined;
        });
      }
      // we should always clear region filter on clear all button
      if (currentFiltersState['region']) newState['region'] = undefined;
      return newState;
    },
    [activeFilters]
  );

  const clearFilterUrl = useCallback(
    (filterName?: TopicFilterNames | TopicFilterNames[]) => {
      let newState: ITopicFilterState = currentFiltersState;
      if (!isArray(filterName)) {
        newState = filterName
          ? {
              ...currentFiltersState,
              [filterName]: undefined,
            }
          : deleteActiveFilters(currentFiltersState);
      }

      if (isArray(filterName)) {
        const newArrayState = currentFiltersState;
        filterName.forEach((fn: TopicFilterNames) => {
          newArrayState[fn] = undefined;
        });

        newState = newArrayState;
      }

      dispatch(saveTopicFilters(newState));
      dispatch(saveTopicFiltersRemote(newState));

      history.replace({ search: stringify(newState) });
    },
    [history, dispatch, currentFiltersState, deleteActiveFilters]
  );

  return [setFilterUrl, clearFilterUrl];
}
